import { AsyncLocalStorage } from 'node:async_hooks';
import { TripWire } from '../agent/trip-wire';
import type { ActorSignal } from '../auth/ee';
import { RequestContext } from '../di';
import { MastraError, MastraNonRetryableError, ErrorDomain, ErrorCategory } from '../error';
import type { SerializedError } from '../error';
import { getErrorFromUnknown } from '../error/utils.js';
import type { PubSub } from '../events/pubsub';
import type { ObservabilityContext, Span, SpanType, TracingPolicy } from '../observability';
import { createObservabilityContext, resolveExportedSpanId } from '../observability';
import { MASTRA_AUTH_TOKEN_KEY } from '../request-context';
import { deepEqual } from '../utils/deep-equal';
import type { ExecutionGraph } from './execution-engine';
import { ExecutionEngine } from './execution-engine';
import type {
  ExecuteConditionalParams,
  ExecuteForeachParams,
  ExecuteLoopParams,
  ExecuteParallelParams,
} from './handlers/control-flow';
import {
  executeConditional as executeConditionalHandler,
  executeForeach as executeForeachHandler,
  executeLoop as executeLoopHandler,
  executeParallel as executeParallelHandler,
} from './handlers/control-flow';
import type { ExecuteEntryParams, PersistStepUpdateParams } from './handlers/entry';
import { executeEntry as executeEntryHandler, persistStepUpdate as persistStepUpdateHandler } from './handlers/entry';
import type { ExecuteSleepParams, ExecuteSleepUntilParams } from './handlers/sleep';
import { executeSleep as executeSleepHandler, executeSleepUntil as executeSleepUntilHandler } from './handlers/sleep';
import type { ExecuteStepParams } from './handlers/step';
import { executeStep as executeStepHandler } from './handlers/step';
import type { ConditionFunction, ConditionFunctionParams, Step } from './step';
import { createMappingStep, createStepFromAgent, createStepFromClassifier, createStepFromTool } from './step-factories';
import type {
  FormattedWorkflowResult,
  DefaultEngineType,
  EntryExecutionResult,
  ExecutionContext,
  MutableContext,
  OutputWriter,
  RestartExecutionParams,
  SerializedStepFlowEntry,
  SingleStepEntry,
  StepExecutionResult,
  StepFailure,
  StepFlowEntry,
  StepResult,
  StepTripwireInfo,
  TimeTravelExecutionParams,
  WorkflowRunStatus,
} from './types';
// Used by the per-type execute methods (executeAgent/executeTool/executeMapping)
// to build a runnable step from a declarative entry.
import { abortableSleep, getRestartStartIndex, getSingleStepEntryId, omitPriorCompletionFields } from './utils';

// Re-export ExecutionContext for backwards compatibility
export type { ExecutionContext } from './types';

/** Params for the per-type execute methods: the same context `executeStep` takes,
 * with the declarative graph entry instead of a pre-built `step`. */
export type ExecuteAgentParams = Omit<ExecuteStepParams, 'step'> & {
  entry: Extract<SingleStepEntry, { type: 'agent' }>;
};
export type ExecuteToolParams = Omit<ExecuteStepParams, 'step'> & { entry: Extract<SingleStepEntry, { type: 'tool' }> };
export type ExecuteClassifierParams = Omit<ExecuteStepParams, 'step'> & {
  entry: Extract<SingleStepEntry, { type: 'classifier' }>;
};
export type ExecuteMappingParams = Omit<ExecuteStepParams, 'step'> & {
  entry: Extract<SingleStepEntry, { type: 'mapping' }>;
};

const retryCountStorage = new AsyncLocalStorage<number>();

/**
 * Default implementation of the ExecutionEngine
 */
export class DefaultExecutionEngine extends ExecutionEngine {
  /**
   * Tracks the last workflow status this engine successfully persisted for a
   * given run. Populated by `persistStepUpdate` after each write.
   *
   * Used to guard against overwriting a `suspended` / `paused` snapshot with a
   * later `running` update from the same run (e.g. during resume, when the
   * loop transitions suspended → running mid-execution and any step-update
   * write would otherwise clobber the suspend record).
   *
   * Process-local by design: on a crash the map is empty, but any run that
   * was in-flight (`running`) after crash will show `running` in storage
   * because we now persist that status — which is exactly what boot-time
   * recovery relies on.
   */
  protected lastPersistedStatusByRun = new Map<string, WorkflowRunStatus>();

  /** Returns the last status persisted for a given run in this process, if any. */
  getLastPersistedStatus(runId: string): WorkflowRunStatus | undefined {
    return this.lastPersistedStatusByRun.get(runId);
  }

  /** Records the last status persisted for a given run in this process. */
  setLastPersistedStatus(runId: string, status: WorkflowRunStatus): void {
    this.lastPersistedStatusByRun.set(runId, status);
  }

  /** Clears the last-persisted-status entry for a run (used on run cleanup). */
  clearLastPersistedStatus(runId: string): void {
    this.lastPersistedStatusByRun.delete(runId);
  }

  /** Returns the current step's zero-based retry attempt, or zero outside an attempt. */
  getOrGenerateRetryCount(_stepId: Step['id']) {
    return retryCountStorage.getStore() ?? 0;
  }

  // =============================================================================
  // Execution Engine Hooks
  // These methods can be overridden by subclasses to customize execution behavior
  // =============================================================================

  /**
   * Check if a step is a nested workflow that requires special handling.
   * Override this in subclasses to detect platform-specific workflow types.
   *
   * @param _step - The step to check
   * @returns true if the step is a nested workflow, false otherwise
   */
  isNestedWorkflowStep(_step: Step<any, any, any>): boolean {
    return false;
  }

  /**
   * Execute the sleep duration. Override to use platform-specific sleep primitives.
   *
   * @param duration - The duration to sleep in milliseconds
   * @param _sleepId - Unique identifier for this sleep operation
   * @param _workflowId - The workflow ID (for constructing platform-specific IDs)
   */
  async executeSleepDuration(
    duration: number,
    _sleepId: string,
    _workflowId: string,
    abortSignal?: AbortSignal,
  ): Promise<void> {
    await abortableSleep(duration, abortSignal);
  }

  /**
   * Execute sleep until a specific date. Override to use platform-specific sleep primitives.
   *
   * @param date - The date to sleep until
   * @param _sleepUntilId - Unique identifier for this sleep operation
   * @param _workflowId - The workflow ID (for constructing platform-specific IDs)
   */
  async executeSleepUntilDate(
    date: Date,
    _sleepUntilId: string,
    _workflowId: string,
    abortSignal?: AbortSignal,
  ): Promise<void> {
    await abortableSleep(date.getTime() - Date.now(), abortSignal);
  }

  /**
   * Wrap a durable operation (like dynamic sleep function evaluation).
   * Override to add platform-specific durability.
   *
   * @param _operationId - Unique identifier for this operation
   * @param operationFn - The function to execute
   * @returns The result of the operation
   */
  async wrapDurableOperation<T>(_operationId: string, operationFn: () => Promise<T>): Promise<T> {
    return operationFn();
  }

  /**
   * Get the engine context to pass to step execution functions.
   * Override to provide platform-specific engine primitives (e.g., Inngest step).
   *
   * @returns An object containing engine-specific context
   */
  getEngineContext(): Record<string, any> {
    return {};
  }

  /**
   * Evaluate a single condition for conditional execution.
   * Override to add platform-specific durability (e.g., Inngest step.run wrapper).
   *
   * @param conditionFn - The condition function to evaluate
   * @param index - The index of this condition
   * @param context - The execution context for the condition
   * @param operationId - Unique identifier for this operation
   * @returns The index if condition is truthy, null otherwise
   */
  async evaluateCondition(
    conditionFn: ConditionFunction<any, any, any, any, any, DefaultEngineType>,
    index: number,
    context: ConditionFunctionParams<any, any, any, any, any, DefaultEngineType>,
    operationId: string,
  ): Promise<number | null> {
    return this.wrapDurableOperation(operationId, async () => {
      const result = await conditionFn(context);
      return result ? index : null;
    });
  }

  /**
   * Handle step execution start - emit events and return start timestamp.
   * Override to add platform-specific durability (e.g., Inngest step.run wrapper).
   *
   * @param params - Parameters for step start
   * @returns The start timestamp (used by some engines like Inngest)
   */
  async onStepExecutionStart(params: {
    step: Step<string, any, any>;
    inputData: any;
    pubsub: PubSub;
    executionContext: ExecutionContext;
    stepCallId: string;
    stepInfo: Record<string, any>;
    operationId: string;
    skipEmits?: boolean;
  }): Promise<number> {
    // Nothing to publish, so avoid spending a durable operation on a bare timestamp.
    if (params.skipEmits) {
      return Date.now();
    }
    return this.wrapDurableOperation(params.operationId, async () => {
      const startedAt = Date.now();
      if (!params.skipEmits) {
        await params.pubsub.publish(`workflow.events.v2.${params.executionContext.runId}`, {
          type: 'watch',
          runId: params.executionContext.runId,
          data: {
            type: 'workflow-step-start',
            payload: {
              id: params.step.id,
              stepCallId: params.stepCallId,
              ...omitPriorCompletionFields(params.stepInfo),
            },
          },
        });
      }
      return startedAt;
    });
  }

  /**
   * Execute a nested workflow step. Override to use platform-specific workflow invocation.
   * This hook is called when isNestedWorkflowStep returns true.
   *
   * Default behavior: returns null to indicate the base executeStep should handle it normally.
   * Inngest overrides this to use inngestStep.invoke() for nested workflows.
   *
   * @param params - Parameters for nested workflow execution
   * @returns StepResult if handled, null if should use default execution
   */
  async executeWorkflowStep(
    _params: ObservabilityContext & {
      step: Step<string, any, any>;
      stepResults: Record<string, StepResult<any, any, any, any>>;
      executionContext: ExecutionContext;
      resume?: {
        steps: string[];
        resumePayload: any;
        runId?: string;
      };
      timeTravel?: TimeTravelExecutionParams;
      prevOutput: any;
      inputData: any;
      pubsub: PubSub;
      startedAt: number;
      abortController: AbortController;
      requestContext: RequestContext;
      actor?: ActorSignal;
      outputWriter?: OutputWriter;
      stepSpan?: Span<SpanType.WORKFLOW_STEP>;
      perStep?: boolean;
    },
  ): Promise<StepResult<any, any, any, any> | null> {
    // Default: return null to use standard execution
    // Subclasses (like Inngest) override to use platform-specific invocation
    return null;
  }

  // =============================================================================
  // Span Lifecycle Hooks
  // These methods can be overridden by subclasses (e.g., Inngest) to make span
  // creation/end durable across workflow replays.
  // =============================================================================

  /**
   * Create a child span for a workflow step.
   * Override to add durability (e.g., Inngest memoization).
   *
   * Default: creates span directly via parent span's createChildSpan.
   *
   * @param params - Parameters for span creation
   * @returns The created span, or undefined if no parent span or tracing disabled
   */
  async createStepSpan(params: {
    parentSpan: Span<SpanType> | undefined;
    stepId: string;
    operationId: string;
    options: {
      name: string;
      type: SpanType;
      input?: unknown;
      entityType?: string;
      entityId?: string;
      attributes?: Record<string, unknown>;
      tracingPolicy?: TracingPolicy;
      requestContext?: RequestContext;
    };
    executionContext: ExecutionContext;
  }): Promise<Span<SpanType> | undefined> {
    // Default: create span directly (no durability)
    return params.parentSpan?.createChildSpan(params.options as any);
  }

  /**
   * End a workflow step span.
   * Override to add durability (e.g., Inngest memoization).
   *
   * Default: calls span.end() directly.
   *
   * @param params - Parameters for ending the span
   */
  async endStepSpan(params: {
    span: Span<SpanType> | undefined;
    operationId: string;
    endOptions: {
      output?: unknown;
      attributes?: Record<string, unknown>;
    };
  }): Promise<void> {
    // Default: end span directly (no durability)
    params.span?.end(params.endOptions as any);
  }

  /**
   * Record an error on a workflow step span.
   * Override to add durability (e.g., Inngest memoization).
   *
   * Default: calls span.error() directly.
   *
   * @param params - Parameters for recording the error
   */
  async errorStepSpan(params: {
    span: Span<SpanType> | undefined;
    operationId: string;
    errorOptions: {
      error: Error;
      attributes?: Record<string, unknown>;
    };
  }): Promise<void> {
    // Default: error span directly (no durability)
    params.span?.error(params.errorOptions as any);
  }

  /**
   * Create a generic child span (for control-flow operations like parallel, conditional, loop).
   * Override to add durability (e.g., Inngest memoization).
   *
   * Default: creates span directly via parent span's createChildSpan.
   *
   * @param params - Parameters for span creation
   * @returns The created span, or undefined if no parent span or tracing disabled
   */
  async createChildSpan(params: {
    parentSpan: Span<SpanType> | undefined;
    operationId: string;
    options: {
      name: string;
      type: SpanType;
      input?: unknown;
      attributes?: Record<string, unknown>;
      tracingPolicy?: TracingPolicy;
    };
    executionContext: ExecutionContext;
  }): Promise<Span<SpanType> | undefined> {
    // Default: create span directly (no durability)
    return params.parentSpan?.createChildSpan(params.options as any);
  }

  /**
   * End a generic child span (for control-flow operations).
   * Override to add durability (e.g., Inngest memoization).
   *
   * Default: calls span.end() directly.
   *
   * @param params - Parameters for ending the span
   */
  async endChildSpan(params: {
    span: Span<SpanType> | undefined;
    operationId: string;
    endOptions?: {
      output?: unknown;
      attributes?: Record<string, unknown>;
    };
  }): Promise<void> {
    // Default: end span directly (no durability)
    params.span?.end(params.endOptions as any);
  }

  /**
   * Record an error on a generic child span (for control-flow operations).
   * Override to add durability (e.g., Inngest memoization).
   *
   * Default: calls span.error() directly.
   *
   * @param params - Parameters for recording the error
   */
  async errorChildSpan(params: {
    span: Span<SpanType> | undefined;
    operationId: string;
    errorOptions: {
      error: Error;
      attributes?: Record<string, unknown>;
    };
  }): Promise<void> {
    // Default: error span directly (no durability)
    params.span?.error(params.errorOptions as any);
  }

  /**
   * Execute a step with retry logic.
   * Default engine: handles retries internally with a loop.
   * Inngest engine: overrides to throw RetryAfterError for external retry handling.
   *
   * @param stepId - Unique identifier for the step (used for durability)
   * @param runStep - The step execution function to run
   * @param params - Retry parameters and context
   * @returns Discriminated union: { ok: true, result: T } or { ok: false, error: ... }
   */
  async executeStepWithRetry<T>(
    stepId: string,
    runStep: () => Promise<T>,
    params: {
      retries: number;
      delay: number;
      stepSpan?: Span<SpanType>;
      workflowId: string;
      runId: string;
    },
  ): Promise<
    | {
        ok: true;
        result: T;
      }
    | {
        ok: false;
        error: {
          status: 'failed';
          error: Error;
          endedAt: number;
          nonRetryable?: true;
          tripwire?: StepTripwireInfo;
        };
      }
  > {
    for (let i = 0; i < params.retries + 1; i++) {
      if (i > 0 && params.delay) {
        await new Promise(resolve => setTimeout(resolve, params.delay));
      }
      try {
        const result = await retryCountStorage.run(i, () => this.wrapDurableOperation(stepId, runStep));
        return { ok: true, result };
      } catch (e) {
        const isNonRetryable = e instanceof MastraNonRetryableError;

        if (isNonRetryable || i === params.retries) {
          // Use getErrorFromUnknown directly on the original error to preserve custom properties
          const errorInstance = getErrorFromUnknown(e, {
            serializeStack: false,
            fallbackMessage: 'Unknown step execution error',
          });

          // Log the error for observability
          const mastraError = new MastraError(
            {
              id: 'WORKFLOW_STEP_INVOKE_FAILED',
              domain: ErrorDomain.MASTRA_WORKFLOW,
              category: ErrorCategory.USER,
              details: { workflowId: params.workflowId, runId: params.runId, stepId },
            },
            errorInstance,
          );
          this.logger?.trackException(mastraError);
          this.logger?.error(`Error executing step ${stepId}: ` + errorInstance?.stack);

          params.stepSpan?.error({
            error: mastraError,
            attributes: { status: 'failed' },
          });

          return {
            ok: false,
            error: {
              status: 'failed',
              error: errorInstance,
              endedAt: Date.now(),
              ...(isNonRetryable && { nonRetryable: true as const }),
              // Preserve TripWire data as plain object for proper serialization
              tripwire:
                e instanceof TripWire
                  ? {
                      reason: e.message,
                      retry: e.options?.retry,
                      metadata: e.options?.metadata,
                      processorId: e.processorId,
                    }
                  : undefined,
            },
          };
        }
        // Continue to next retry
      }
    }
    // Should never reach here, but TypeScript needs it
    return { ok: false, error: { status: 'failed', error: new Error('Unknown error'), endedAt: Date.now() } };
  }

  /**
   * Format an error for the workflow result.
   * Override to customize error formatting (e.g., include stack traces).
   */
  protected formatResultError(error: Error | unknown, lastOutput: StepResult<any, any, any, any>): SerializedError {
    const outputError = (lastOutput as StepFailure<any, any, any, any>)?.error;
    const errorSource = error || outputError;
    const errorInstance = getErrorFromUnknown(errorSource, {
      serializeStack: false,
      fallbackMessage: 'Unknown workflow error',
    });
    return errorInstance.toJSON();
  }

  protected async fmtReturnValue<TOutput>(
    _pubsub: PubSub,
    stepResults: Record<string, StepResult<any, any, any, any>>,
    lastOutput: StepResult<any, any, any, any>,
    error?: Error | unknown,
    stepExecutionPath?: string[],
  ): Promise<TOutput> {
    // Strip nestedRunId from metadata (internal tracking for nested workflow retrieval)
    const cleanStepResults: Record<string, StepResult<any, any, any, any>> = {};
    for (const [stepId, stepResult] of Object.entries(stepResults)) {
      if (stepResult && typeof stepResult === 'object' && !Array.isArray(stepResult) && 'metadata' in stepResult) {
        const { metadata, ...rest } = stepResult as any;
        if (metadata) {
          const { nestedRunId: _nestedRunId, ...userMetadata } = metadata;
          if (Object.keys(userMetadata).length > 0) {
            cleanStepResults[stepId] = { ...rest, metadata: userMetadata };
          } else {
            cleanStepResults[stepId] = rest;
          }
        } else {
          cleanStepResults[stepId] = stepResult;
        }
      } else {
        cleanStepResults[stepId] = stepResult;
      }
    }

    const base: FormattedWorkflowResult = {
      status: lastOutput.status,
      steps: cleanStepResults,
      input: cleanStepResults.input,
    };

    if (stepExecutionPath) {
      base.stepExecutionPath = stepExecutionPath;

      // Create a shallow copy of steps to modify without affecting the original reference
      const optimizedSteps: Record<string, StepResult<any, any, any, any>> = { ...cleanStepResults };

      let previousOutput: unknown;
      let hasPreviousOutput = 'input' in cleanStepResults;
      if (hasPreviousOutput) {
        previousOutput = cleanStepResults.input;
      }

      for (const stepId of stepExecutionPath) {
        const originalStep = cleanStepResults[stepId];
        if (!originalStep) continue;

        // Clone step result to avoid mutating the original object in memory
        const optimizedStep = { ...originalStep };

        // Remove payload if it matches the output of the previous step (structural comparison
        // handles deserialized data where reference equality would fail)
        let payloadMatchesPrevious = false;
        if (hasPreviousOutput) {
          try {
            payloadMatchesPrevious =
              optimizedStep.payload === previousOutput || deepEqual(optimizedStep.payload, previousOutput);
          } catch {
            // Values that cannot be structurally compared are treated as not matching.
          }
        }
        if (payloadMatchesPrevious) {
          delete optimizedStep.payload;
        }

        if (optimizedStep.status === 'success') {
          previousOutput = optimizedStep.output;
          hasPreviousOutput = true;
        }

        optimizedSteps[stepId] = optimizedStep;
      }

      base.steps = optimizedSteps;
    }

    if (lastOutput.status === 'success') {
      base.result = lastOutput.output;
    } else if (lastOutput.status === 'failed') {
      // Check if the failure was due to a TripWire
      const tripwireData = lastOutput?.tripwire;
      if (tripwireData instanceof TripWire) {
        // Use 'tripwire' status instead of 'failed' for tripwire errors (TripWire instance)
        base.status = 'tripwire';
        base.tripwire = {
          reason: tripwireData.message,
          retry: tripwireData.options?.retry,
          metadata: tripwireData.options?.metadata,
          processorId: tripwireData.processorId,
        };
      } else if (tripwireData && typeof tripwireData === 'object' && 'reason' in tripwireData) {
        // Use 'tripwire' status for plain tripwire data objects (already serialized)
        base.status = 'tripwire';
        base.tripwire = tripwireData;
      } else {
        base.error = this.formatResultError(error, lastOutput);
      }
    } else if (lastOutput.status === 'suspended') {
      const suspendPayload: Record<string, any> = {};
      const suspendedStepIds = Object.entries(stepResults).flatMap(([stepId, stepResult]) => {
        if (stepResult?.status === 'suspended') {
          const { __workflow_meta, ...rest } = stepResult?.suspendPayload ?? {};
          suspendPayload[stepId] = rest;
          const nestedPath = __workflow_meta?.path;
          return nestedPath ? [[stepId, ...nestedPath]] : [[stepId]];
        }

        return [];
      });
      base.suspended = suspendedStepIds;
      base.suspendPayload = suspendPayload;
    }

    return base as TOutput;
  }

  // =============================================================================
  // Context Serialization Helpers
  // =============================================================================

  /**
   * Serialize a RequestContext Map to a plain object for JSON serialization.
   * Used by durable execution engines to persist context across step replays.
   */
  serializeRequestContext(requestContext: RequestContext): Record<string, any> {
    let obj: Record<string, any>;
    if (typeof requestContext.toJSON === 'function') {
      obj = requestContext.toJSON();
    } else {
      obj = {};
      requestContext.forEach((value, key) => {
        obj[key] = value;
      });
    }
    // Never persist the framework-managed bearer token in durable snapshots.
    // A resumed authenticated request supplies its own fresh live token.
    delete obj[MASTRA_AUTH_TOKEN_KEY];
    return obj;
  }

  /**
   * Deserialize a plain object back to a RequestContext instance.
   * Used to restore context after durable execution replay.
   */
  protected deserializeRequestContext(obj: Record<string, any>): RequestContext {
    const ctx = new RequestContext();
    for (const [key, value] of Object.entries(obj)) {
      ctx.set(key, value);
    }
    return ctx;
  }

  /**
   * Whether this engine requires requestContext to be serialized for durable operations.
   * Default engine passes by reference (no serialization needed).
   * Inngest engine overrides to return true (serialization required for memoization).
   */
  requiresDurableContextSerialization(): boolean {
    return false;
  }

  /**
   * Build MutableContext from current execution state.
   * This extracts only the fields that can change during step execution.
   */
  buildMutableContext(executionContext: ExecutionContext): MutableContext {
    return {
      state: executionContext.state,
      suspendedPaths: executionContext.suspendedPaths,
      resumeLabels: executionContext.resumeLabels,
    };
  }

  /**
   * Apply mutable context changes back to the execution context.
   */
  applyMutableContext(executionContext: ExecutionContext, mutableContext: MutableContext): void {
    Object.assign(executionContext.state, mutableContext.state);
    Object.assign(executionContext.suspendedPaths, mutableContext.suspendedPaths);
    Object.assign(executionContext.resumeLabels, mutableContext.resumeLabels);
  }

  /**
   * Executes a workflow run with the provided execution graph and input
   * @param graph The execution graph to execute
   * @param input The input data for the workflow
   * @returns A promise that resolves to the workflow output
   */
  async execute<TState, TInput, TOutput>(params: {
    workflowId: string;
    runId: string;
    resourceId?: string;
    disableScorers?: boolean;
    graph: ExecutionGraph;
    serializedStepGraph: SerializedStepFlowEntry[];
    input?: TInput;
    initialState?: TState;
    restart?: RestartExecutionParams;
    timeTravel?: TimeTravelExecutionParams;
    resume?: {
      steps: string[];
      stepResults: Record<string, StepResult<any, any, any, any>>;
      resumePayload: any;
      resumePath: number[];
      stepExecutionPath?: string[];
      label?: string;
      forEachIndex?: number;
    };
    pubsub: PubSub;
    retryConfig?: {
      attempts?: number;
      delay?: number;
    };
    requestContext: RequestContext;
    actor?: ActorSignal;
    workflowSpan?: Span<SpanType.WORKFLOW_RUN>;
    abortController: AbortController;
    outputWriter?: OutputWriter;
    format?: 'legacy' | 'vnext' | undefined;
    outputOptions?: {
      includeState?: boolean;
      includeResumeLabels?: boolean;
    };
    perStep?: boolean;
    /** Trace IDs for creating child spans in durable execution */
    tracingIds?: {
      traceId: string;
      workflowSpanId: string;
    };
  }): Promise<TOutput> {
    const {
      workflowId,
      runId,
      resourceId,
      graph,
      input,
      initialState,
      resume,
      retryConfig,
      workflowSpan,
      disableScorers,
      restart,
      timeTravel,
      perStep,
    } = params;
    const { attempts = 0, delay = 0 } = retryConfig ?? {};
    const steps = graph.steps;

    if (steps.length === 0) {
      const empty_graph_error = new MastraError({
        id: 'WORKFLOW_EXECUTE_EMPTY_GRAPH',
        text: 'Workflow must have at least one step',
        domain: ErrorDomain.MASTRA_WORKFLOW,
        category: ErrorCategory.USER,
      });

      workflowSpan?.error({ error: empty_graph_error });
      throw empty_graph_error;
    }

    let startIdx = 0;
    let entryRestart = restart;
    if (timeTravel) {
      startIdx = timeTravel.executionPath[0]!;
      timeTravel.executionPath.shift();
    } else if (restart) {
      startIdx = getRestartStartIndex(steps, restart);
      if (startIdx === restart.activePaths[0]) {
        restart.activePaths.shift();
      } else {
        // The checkpoint's entry had already finished and nothing is in flight,
        // so the remaining entries run as a normal run rather than a recovery.
        entryRestart = undefined;
      }
    } else if (resume?.resumePath) {
      startIdx = resume.resumePath[0]!;
      resume.resumePath.shift();
    }

    const stepResults: Record<string, any> = timeTravel?.stepResults ||
      restart?.stepResults ||
      resume?.stepResults || { input };
    let stepExecutionPath: string[] =
      timeTravel?.stepExecutionPath || restart?.stepExecutionPath || resume?.stepExecutionPath || [];
    let lastOutput: any;
    let lastState: Record<string, any> = timeTravel?.state ?? restart?.state ?? initialState ?? {};
    let lastExecutionContext: ExecutionContext | undefined;
    let currentRequestContext = params.requestContext;
    for (let i = startIdx; i < steps.length; i++) {
      if (params.abortController.signal.aborted) {
        await this.persistStepUpdate({
          workflowId,
          runId,
          resourceId,
          stepResults,
          serializedStepGraph: params.serializedStepGraph,
          executionContext: lastExecutionContext || {
            workflowId,
            runId,
            executionPath: [i],
            stepExecutionPath,
            activeStepsPath: {},
            suspendedPaths: {},
            resumeLabels: {},
            retryConfig: { attempts, delay },
            format: params.format,
            state: lastState ?? initialState,
            tracingIds: params.tracingIds,
          },
          workflowStatus: 'canceled',
          requestContext: currentRequestContext,
          phase: 'canceled',
        });

        workflowSpan?.end({
          attributes: {
            status: 'canceled',
          },
        });

        const formattedResult = await this.fmtReturnValue<any>(
          params.pubsub,
          stepResults,
          { status: 'canceled' },
          undefined,
          stepExecutionPath,
        );

        await this.invokeLifecycleCallbacks({
          status: 'canceled',
          result: undefined,
          error: undefined,
          steps: formattedResult.steps,
          tripwire: undefined,
          runId,
          workflowId,
          resourceId,
          input,
          requestContext: currentRequestContext,
          state: lastState,
          stepExecutionPath,
        });

        return {
          ...formattedResult,
          runId,
          ...(params.outputOptions?.includeState ? { state: lastState } : {}),
        } as any;
      }

      const entry = steps[i]!;

      const executionContext: ExecutionContext = {
        workflowId,
        runId,
        executionPath: [i],
        stepExecutionPath,
        activeStepsPath: {},
        suspendedPaths: {},
        resumeLabels: {},
        retryConfig: { attempts, delay },
        format: params.format,
        state: lastState ?? initialState,
        // Tracing IDs for durable span operations (Inngest)
        tracingIds: params.tracingIds,
      };
      lastExecutionContext = executionContext;

      lastOutput = await this.executeEntry({
        workflowId,
        runId,
        resourceId,
        entry,
        executionContext,
        serializedStepGraph: params.serializedStepGraph,
        prevStep: steps[i - 1]!,
        stepResults,
        resume,
        timeTravel,
        restart: entryRestart,
        ...createObservabilityContext({ currentSpan: workflowSpan }),
        abortController: params.abortController,
        pubsub: params.pubsub,
        requestContext: currentRequestContext,
        actor: params.actor,
        outputWriter: params.outputWriter,
        disableScorers,
        perStep,
      });

      // Apply mutable context changes from entry execution
      this.applyMutableContext(executionContext, lastOutput.mutableContext);
      lastState = lastOutput.mutableContext.state;
      // Update requestContext from step result (only for engines that serialize context)
      // Default engine keeps the original reference, Inngest deserializes from memoized result
      if (this.requiresDurableContextSerialization() && lastOutput.requestContext) {
        currentRequestContext = this.deserializeRequestContext(lastOutput.requestContext);
      }

      // if step result is not success, stop and return
      if (lastOutput.result.status !== 'success') {
        if (lastOutput.result.status === 'bailed') {
          lastOutput.result.status = 'success';
        }

        const result = (await this.fmtReturnValue(
          params.pubsub,
          stepResults,
          lastOutput.result,
          undefined,
          stepExecutionPath,
        )) as any;

        // Capture tracing context for suspend to enable span linking on resume.
        // On resume this spanId becomes the resumed span's parentSpanId, so it has to
        // name a span that reached exporters — an internal/excluded workflow span is
        // never stored, and the resumed span's exported children would inherit it and
        // land as orphans. Undefined when nothing in the chain is exportable, which
        // correctly makes those children trace roots instead.
        const persistTracingContext =
          result.status === 'suspended' && workflowSpan
            ? {
                traceId: workflowSpan.traceId,
                spanId: resolveExportedSpanId(workflowSpan),
                parentSpanId: workflowSpan.getParentSpanId(),
              }
            : {};

        await this.persistStepUpdate({
          workflowId,
          runId,
          resourceId,
          stepResults: lastOutput.stepResults,
          serializedStepGraph: params.serializedStepGraph,
          executionContext,
          workflowStatus: result.status,
          result: result.result,
          error: result.error,
          requestContext: currentRequestContext,
          tracingContext: persistTracingContext,
          phase: 'terminal',
        });

        if (result.error) {
          workflowSpan?.error({
            error: result.error,
            attributes: {
              status: result.status,
            },
          });
        } else {
          workflowSpan?.end({
            output: result.result,
            attributes: {
              status: result.status,
            },
          });
        }

        if (lastOutput.result.status !== 'paused') {
          // Invoke lifecycle callbacks before returning
          await this.invokeLifecycleCallbacks({
            status: result.status,
            result: result.result,
            error: result.error,
            steps: result.steps,
            tripwire: result.tripwire,
            runId,
            workflowId,
            resourceId,
            input,
            requestContext: currentRequestContext,
            state: lastState,
            stepExecutionPath,
          });
        }

        if (lastOutput.result.status === 'paused') {
          await params.pubsub.publish(`workflow.events.v2.${runId}`, {
            type: 'watch',
            runId,
            data: { type: 'workflow-paused', payload: {} },
          });
        }

        // Drop the last-persisted-status tracker for early terminal exits
        // (failed, canceled, tripwire) so the map does not grow unbounded.
        // Suspended and paused runs keep their entry so a subsequent resume
        // in the same process still sees the correct status and refuses to
        // overwrite it with `running` mid-resume.
        if (lastOutput.result.status !== 'suspended' && lastOutput.result.status !== 'paused') {
          this.clearLastPersistedStatus(runId);
        }

        return {
          ...result,
          runId,
          ...(lastOutput.result.status === 'suspended' && params.outputOptions?.includeResumeLabels
            ? { resumeLabels: lastOutput.mutableContext.resumeLabels }
            : {}),
          ...(params.outputOptions?.includeState ? { state: lastState } : {}),
        };
      }

      if (perStep) {
        const result = (await this.fmtReturnValue(
          params.pubsub,
          stepResults,
          lastOutput.result,
          undefined,
          stepExecutionPath,
        )) as any;
        await this.persistStepUpdate({
          workflowId,
          runId,
          resourceId,
          stepResults: lastOutput.stepResults,
          serializedStepGraph: params.serializedStepGraph,
          executionContext: lastExecutionContext,
          workflowStatus: 'paused',
          requestContext: currentRequestContext,
          phase: 'per-step',
        });

        await params.pubsub.publish(`workflow.events.v2.${runId}`, {
          type: 'watch',
          runId,
          data: { type: 'workflow-paused', payload: {} },
        });

        workflowSpan?.end({
          attributes: {
            status: 'paused',
          },
        });

        delete result.result;

        return {
          ...result,
          runId,
          status: 'paused',
          ...(params.outputOptions?.includeState ? { state: lastState } : {}),
        };
      }
    }

    if (lastOutput === undefined) {
      // Restarted from a checkpoint written after the final entry finished, so
      // nothing was left to run. Complete the run with that entry's saved output,
      // shaped the way the entry returns it: parallel and conditional keep only the
      // branches that succeeded, like executeParallel and executeConditional.
      const lastIdx = steps.length - 1;
      const lastEntry = steps[lastIdx]!;
      const output =
        lastEntry.type === 'parallel' || lastEntry.type === 'conditional'
          ? Object.fromEntries(
              lastEntry.steps
                .map(getSingleStepEntryId)
                .filter(id => stepResults[id]?.status === 'success')
                .map(id => [id, stepResults[id].output]),
            )
          : this.getStepOutput(stepResults, lastEntry);
      lastOutput = { result: { status: 'success', output }, stepResults };
      lastExecutionContext = {
        workflowId,
        runId,
        executionPath: [lastIdx],
        stepExecutionPath,
        activeStepsPath: {},
        suspendedPaths: {},
        resumeLabels: {},
        retryConfig: { attempts, delay },
        format: params.format,
        state: lastState,
        tracingIds: params.tracingIds,
      };
    }

    // after all steps are successful, return result
    const result = (await this.fmtReturnValue(
      params.pubsub,
      stepResults,
      lastOutput.result,
      undefined,
      stepExecutionPath,
    )) as any;
    await this.persistStepUpdate({
      workflowId,
      runId,
      resourceId,
      stepResults: lastOutput.stepResults,
      serializedStepGraph: params.serializedStepGraph,
      executionContext: lastExecutionContext!,
      workflowStatus: result.status,
      result: result.result,
      error: result.error,
      requestContext: currentRequestContext,
      phase: 'workflow-end',
    });

    workflowSpan?.end({
      output: result.result,
      attributes: {
        status: result.status,
      },
    });

    await this.invokeLifecycleCallbacks({
      status: result.status,
      result: result.result,
      error: result.error,
      steps: result.steps,
      tripwire: result.tripwire,
      runId,
      workflowId,
      resourceId,
      input,
      requestContext: currentRequestContext,
      state: lastState,
      stepExecutionPath,
    });

    // Drop the last-persisted-status tracker for terminal runs so the map
    // does not grow unbounded across many runs served by the same engine.
    // Suspended runs keep their entry so a subsequent resume in the same
    // process still sees `suspended` and refuses to overwrite it with
    // `running` mid-resume.
    if (result.status !== 'suspended') {
      this.clearLastPersistedStatus(runId);
    }

    if (params.outputOptions?.includeState) {
      return { ...result, runId, state: lastState };
    }
    return { ...result, runId };
  }

  getStepOutput(stepResults: Record<string, any>, step?: StepFlowEntry): any {
    if (!step) {
      return stepResults.input;
    } else if (step.type === 'step') {
      return stepResults[step.step.id]?.output;
    } else if (step.type === 'agent' || step.type === 'tool' || step.type === 'classifier' || step.type === 'mapping') {
      return stepResults[step.id]?.output;
    } else if (step.type === 'sleep' || step.type === 'sleepUntil') {
      return stepResults[step.id]?.output;
    } else if (step.type === 'parallel' || step.type === 'conditional') {
      return step.steps.reduce(
        (acc, entry) => {
          const id = entry.type === 'step' ? entry.step.id : entry.id;
          acc[id] = stepResults[id]?.output;
          return acc;
        },
        {} as Record<string, any>,
      );
    } else if (step.type === 'loop') {
      return stepResults[getSingleStepEntryId(step.step)]?.output;
    } else if (step.type === 'foreach') {
      return stepResults[getSingleStepEntryId(step.step)]?.output;
    }
  }

  async executeSleep(params: ExecuteSleepParams): Promise<void> {
    return executeSleepHandler(this, params);
  }

  async executeSleepUntil(params: ExecuteSleepUntilParams): Promise<void> {
    return executeSleepUntilHandler(this, params);
  }

  async executeStep(params: ExecuteStepParams): Promise<StepExecutionResult> {
    return executeStepHandler(this, params);
  }

  /**
   * Executes a declarative `agent` step: resolves the agent (live ref, else
   * `mastra.getAgentById(agentId)`), builds its runnable step, and runs it through
   * the shared step runner.
   */
  async executeAgent(params: ExecuteAgentParams): Promise<StepExecutionResult> {
    const { entry, ...rest } = params;
    const agent = entry.agent ?? this.mastra?.getAgentById(entry.agentId);
    if (!agent) {
      throw new Error(
        `Agent '${entry.agentId}' not found for workflow step '${entry.id}'. Register the agent on the Mastra instance or pass the agent instance directly.`,
      );
    }
    return this.executeStep({ ...rest, step: { ...createStepFromAgent(agent as any, entry.options), id: entry.id } });
  }

  /**
   * Executes a declarative `tool` step: resolves the tool (live ref, else
   * `mastra.getTool(toolId)`), builds its runnable step, and runs it through the
   * shared step runner.
   */
  async executeTool(params: ExecuteToolParams): Promise<StepExecutionResult> {
    const { entry, ...rest } = params;
    const tool = entry.tool ?? this.mastra?.getTool(entry.toolId);
    if (!tool) {
      throw new Error(
        `Tool '${entry.toolId}' not found for workflow step '${entry.id}'. Pass the tool instance directly.`,
      );
    }
    return this.executeStep({ ...rest, step: { ...createStepFromTool(tool as any, entry.options), id: entry.id } });
  }

  async executeClassifier(params: ExecuteClassifierParams): Promise<StepExecutionResult> {
    const { entry, ...rest } = params;
    let classifier = entry.classifier;
    if (!classifier) {
      try {
        classifier = this.mastra?.getClassifierById(entry.classifierId);
      } catch {
        throw new Error(
          `Classifier '${entry.classifierId}' not found for workflow step '${entry.id}'. Register it with Mastra or pass the classifier instance directly.`,
        );
      }
    }
    if (!classifier) {
      throw new Error(
        `Classifier '${entry.classifierId}' not found for workflow step '${entry.id}'. Register it with Mastra or pass the classifier instance directly.`,
      );
    }
    return this.executeStep({
      ...rest,
      step: createStepFromClassifier(classifier as any, {
        ...entry.options,
        id: entry.id,
      }),
    });
  }

  /**
   * Executes a declarative `mapping` step: builds the mapping step from the
   * declarative config/fn and runs it through the shared step runner.
   */
  async executeMapping(params: ExecuteMappingParams): Promise<StepExecutionResult> {
    const { entry, ...rest } = params;
    return this.executeStep({
      ...rest,
      step: createMappingStep(entry.id, entry.mapConfig),
      entryDescription: entry.description,
      entryMetadata: entry.metadata,
    });
  }

  async executeParallel(params: ExecuteParallelParams): Promise<StepResult<any, any, any, any>> {
    return executeParallelHandler(this, params);
  }

  async executeConditional(params: ExecuteConditionalParams): Promise<StepResult<any, any, any, any>> {
    return executeConditionalHandler(this, params);
  }

  async executeLoop(params: ExecuteLoopParams): Promise<StepResult<any, any, any, any>> {
    return executeLoopHandler(this, params);
  }

  async executeForeach(params: ExecuteForeachParams): Promise<StepResult<any, any, any, any>> {
    return executeForeachHandler(this, params);
  }

  async persistStepUpdate(params: PersistStepUpdateParams): Promise<void> {
    return persistStepUpdateHandler(this, params);
  }

  async executeEntry(params: ExecuteEntryParams): Promise<EntryExecutionResult> {
    return executeEntryHandler(this, params);
  }
}
